import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CreditCard, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerTermsOfPayment = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('customer_terms_of_payment');
  const [viewItem, setViewItem] = useState(null);
  const [termsOfPayment, setTermsOfPayment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentTerm, setCurrentTerm] = useState(null);
  const [formData, setFormData] = useState({
    payment_key: '',
    sales_text: '',
    day_limit: '',
    explanations: ''
  });

  useEffect(() => {
    loadTermsOfPayment();
  }, []);

  const loadTermsOfPayment = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/customer-terms-of-payments`);
      setTermsOfPayment(response.data);
    } catch (error) {
      console.error('Error loading terms of payment:', error);
      toast.error('Failed to load terms of payment');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.payment_key || !formData.sales_text || !formData.day_limit || !formData.explanations) {
      toast.error('Please fill all required fields');
      return;
    }

    const dayLimitNum = parseInt(formData.day_limit);
    if (isNaN(dayLimitNum) || dayLimitNum < 0) {
      toast.error('Day limit must be a positive number');
      return;
    }

    try {
      if (editMode) {
        await axios.put(
          `${BACKEND_URL}/api/customer-terms-of-payments/${currentTerm.payment_key}`,
          {
            sales_text: formData.sales_text,
            day_limit: dayLimitNum,
            explanations: formData.explanations
          }
        );
        toast.success('Terms of Payment updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/customer-terms-of-payments`, {
          ...formData,
          day_limit: dayLimitNum
        });
        toast.success('Terms of Payment created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      loadTermsOfPayment();
    } catch (error) {
      console.error('Error saving terms of payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to save terms of payment');
    }
  };

  const handleEdit = (term) => {
    setCurrentTerm(term);
    setFormData({
      payment_key: term.payment_key,
      sales_text: term.sales_text,
      day_limit: term.day_limit.toString(),
      explanations: term.explanations
    });
    setEditMode(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({ payment_key: '', sales_text: '', day_limit: '', explanations: '' });
    setCurrentTerm(null);
    setEditMode(false);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    resetForm();
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Terms of Payment</h1>
                <p className="text-sm text-gray-600">Configure payment terms and conditions for customers</p>
              </div>
            </div>
            {writable && (<Button
              onClick={() => setShowDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Payment Terms
            </Button>)}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mb-3" />
              <p className="text-sm text-gray-600">Loading payment terms...</p>
            </div>
          ) : termsOfPayment.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CreditCard className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No payment terms yet</h3>
              <p className="text-sm text-gray-600 mb-4">Create your first payment terms</p>
              {writable && (<Button onClick={() => setShowDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Payment Terms
              </Button>)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Key</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales Text</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Day Limit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Explanations</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {termsOfPayment.map((term) => (
                    <tr key={term.payment_key} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{term.payment_key}</span>
                      </td>
                      <td className="px-6 py-4">{term.sales_text}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                          {term.day_limit} days
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs truncate" title={term.explanations}>
                          {term.explanations}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <RecordActions doctype="customer_terms_of_payment" onView={() => setViewItem(term)} onEdit={() => handleEdit(term)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editMode ? 'Edit Payment Terms' : 'Add Payment Terms'}
                </h2>
                <button
                  onClick={handleDialogClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Payment Key *</Label>
                  <Input
                    maxLength={10}
                    value={formData.payment_key}
                    onChange={(e) => setFormData({ ...formData, payment_key: e.target.value.toUpperCase() })}
                    placeholder="e.g., NET30, NET60"
                    disabled={editMode}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 10 characters</p>
                </div>

                <div>
                  <Label>Sales Text *</Label>
                  <Input
                    maxLength={100}
                    value={formData.sales_text}
                    onChange={(e) => setFormData({ ...formData, sales_text: e.target.value })}
                    placeholder="e.g., Net 30 days"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 100 characters</p>
                </div>

                <div>
                  <Label>Day Limit *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.day_limit}
                    onChange={(e) => setFormData({ ...formData, day_limit: e.target.value })}
                    placeholder="e.g., 30"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Number of days for payment</p>
                </div>

                <div>
                  <Label>Explanations *</Label>
                  <Textarea
                    maxLength={255}
                    value={formData.explanations}
                    onChange={(e) => setFormData({ ...formData, explanations: e.target.value })}
                    placeholder="Detailed explanation of payment terms"
                    className="mt-1"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 255 characters</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleDialogClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    {editMode ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default CustomerTermsOfPayment;